import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sprout, Cpu, Skull, Check, ArrowRight } from "lucide-react";
import { Overline } from "../Reveal";

const levels = [
  {
    icon: Sprout,
    tag: "Nivel 01",
    title: "Principiante",
    duration: "6 semanas · CTF de iniciación",
    color: "#22e07a",
    modules: [
      "Fundamentos de redes y Linux",
      "Introducción a la ciberseguridad",
      "Reconocimiento y OSINT básico",
      "Primeros pasos con Kali Linux",
    ],
  },
  {
    icon: Cpu,
    tag: "Nivel 02",
    title: "Medio",
    duration: "10 semanas · Labs prácticos",
    color: "#00f0ff",
    featured: true,
    modules: [
      "Explotación web (OWASP Top 10)",
      "Pentesting de redes e infra",
      "Escalada de privilegios",
      "Scripting ofensivo (Python / Bash)",
    ],
  },
  {
    icon: Skull,
    tag: "Nivel 03",
    title: "Experto",
    duration: "14 semanas · Simulacros reales",
    color: "#b026ff",
    modules: [
      "Red Team & técnicas de evasión",
      "Explotación de binarios / exploiting",
      "Ataques a Active Directory",
      "C2, malware dev & OPSEC",
    ],
  },
];

const Card = ({ lvl, i }) => {
  const Icon = lvl.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -8 }}
      data-testid={`academy-level-${i}`}
      className={`relative flex flex-col rounded-2xl border p-8 transition-colors duration-500 ${
        lvl.featured
          ? "border-cyber-cyan/40 bg-cyber-cyan/[0.06]"
          : "border-white/10 bg-white/[0.03] hover:border-white/25"
      }`}
    >
      {lvl.featured && (
        <span className="absolute -top-3 left-8 rounded-full bg-cyber-cyan px-3 py-1 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-[#04060a]">
          Más popular
        </span>
      )}
      <div className="flex items-center justify-between">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-xl"
          style={{ background: `${lvl.color}1a`, border: `1px solid ${lvl.color}55` }}
        >
          <Icon className="h-6 w-6" style={{ color: lvl.color }} strokeWidth={1.6} />
        </span>
        <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/40">
          {lvl.tag}
        </span>
      </div>

      <h3 className="mt-6 font-head text-2xl font-bold text-white">{lvl.title}</h3>
      <p className="mt-1 font-mono-tech text-xs text-white/50">{lvl.duration}</p>

      <ul className="mt-6 flex-1 space-y-3">
        {lvl.modules.map((m) => (
          <li key={m} className="flex items-start gap-3 font-sans text-sm text-white/70">
            <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: lvl.color }} />
            {m}
          </li>
        ))}
      </ul>

      <Link
        to="/acceso"
        data-testid={`academy-cta-${i}`}
        className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:scale-[1.02] ${
          lvl.featured ? "bg-cyber-cyan text-[#04060a]" : "border border-white/15 text-white/80"
        }`}
      >
        Empezar
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </motion.div>
  );
};

export const Academy = () => (
  <section
    id="formacion"
    className="relative mx-auto max-w-7xl px-5 py-28 md:px-10 md:py-40"
    data-testid="academy"
  >
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div>
        <Overline>Formación</Overline>
        <h2 className="mt-6 max-w-2xl font-head text-4xl font-bold tracking-tight text-white md:text-5xl">
          Conviértete en hacker ético
        </h2>
      </div>
      <p className="max-w-xs font-sans text-sm text-white/50">
        Una ruta de aprendizaje progresiva, de cero a Red Team. Teoría, laboratorios y retos reales en cada nivel.
      </p>
    </div>

    <div className="mt-14 grid gap-6 md:grid-cols-3">
      {levels.map((lvl, i) => (
        <Card key={lvl.title} lvl={lvl} i={i} />
      ))}
    </div>
  </section>
);
